import { intercept, useRequiredContext, useTrackingAsync } from "@ispb/react-hooks"
import { compose } from "ramda"
import { Fragment } from "react"
import { Alert, Badge, Table } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import { formatDate, formatDateTime } from "shared/date"
import IconButton from "ui/components/IconButton"
import IconText from "ui/components/IconText"
import NotFound from "ui/components/NotFound"
import Spacer from "ui/components/Spacer"
import { DEFAULT_TABLE_PROPS } from "ui/config"
import { mapped } from "ui/hoc"
import { EMPTY_ICON, SUCCESS_ICON } from "ui/icons"
import { Mop } from "../namespaces/Mop.js"
import CollectName from "./CollectName.js"
import ExecutionControlPoint from "./ExecutionControlPoint.js"
import { ENERGIZED_COLOR, getExecutionStatus, ICON_COLUMN_STYLE, LOCKED_OUT_COLOR, PublicProcedureContext, useAnonymous } from "./index.js"
import PublicLayout from "./Layout.js"
import { cleanProcedure, PROCEDURE_FRAGMENT } from "./print.js"
import PublicThumbnail from "./PublicThumbnail.js"
import StatusBadge from "./StatusBadge.js"
import StepTable from "./StepTable.js"

const component = compose(
    mapped(() => {
        return {
            passthrough: {},
            map: Mop.object({
                executionId: Mop.nonBlankString()
            }),
        }
    }),
    intercept(props => {
        const procedure = useRequiredContext(PublicProcedureContext)
        const execution = procedure.data.executions.find(_ => _.id === props.executionId)
        if (execution === undefined) {
            return {
                type: "replace",
                element: <NotFound />
            }
        }
        return {
            type: "transform",
            props: {
                execution
            }
        }
    })
)(props => {
    const [anonymous, setAnonymous] = useAnonymous()
    const pin = useRequiredContext(PublicProcedureContext)
    const navigate = useNavigate()
    const finish = useTrackingAsync(async () => {
        const result = await pin.hasura.mutation({
            updateExecutionsByPk: {
                __args: {
                    pkColumns: {
                        id: props.execution.id,
                        companyId: pin.data.companyId
                    },
                    _set: {
                        status: false
                    }
                },
                procedure: PROCEDURE_FRAGMENT,
            }
        })
        pin.setData(cleanProcedure(result.updateExecutionsByPk?.procedure!))
        navigate("../execute")
    })
    const status = getExecutionStatus(props.execution.actions, pin.data.controlPoints)
    const title = "Execution (" + formatDate(props.execution.createdOn) + ")"
    if (anonymous === undefined) {
        return (
            <PublicLayout title={title} subtitle={<StatusBadge {...status} />}>
                <CollectName setName={setAnonymous} />
            </PublicLayout>
        )
    }
    const actions = <IconButton icon={SUCCESS_ICON} onClick={finish.run} busy={finish.isRunning} variant="success">Finish Execution</IconButton>
    return (
        <PublicLayout title={title} subtitle={<StatusBadge {...status} />} actions={actions}>
            <h2>Control Points</h2>
            <StepTable procedure={pin.data} execution={props.execution} actions={(controlPoint, index) => <ExecutionControlPoint execution={props.execution} procedure={pin.data} controlPoint={controlPoint} index={index} executionStatus={status} />} />
            {
                (() => {
                    if (pin.data.procedureHazards.length === 0) {
                        return
                    }
                    else {
                        return (
                            <Fragment>
                                <h2>Hazards</h2>
                                <Table {...DEFAULT_TABLE_PROPS}>
                                    <thead>
                                        <tr>
                                            <th>Photo</th>
                                            <th>Name</th>
                                            <th>Statement</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            pin.data.procedureHazards.map(_ => _.hazard).map(hazard => {
                                                return (
                                                    <tr key={hazard.id}>
                                                        <td style={ICON_COLUMN_STYLE}>{<PublicThumbnail companyId={pin.data.companyId} id={hazard.icon.id} upload={hazard.icon} />}</td>
                                                        <td><Badge bg="" style={{ backgroundColor: "#" + hazard.color }}>{hazard.name}</Badge></td>
                                                        <td>{hazard.statement}</td>
                                                    </tr>
                                                )
                                            })
                                        }
                                    </tbody>
                                </Table>
                            </Fragment>
                        )
                    }
                })()
            }
            <Spacer />
            <h2>Action Log</h2>
            {
                (() => {
                    if (props.execution.actions.length === 0) {
                        return <Alert variant="info"><IconText {...EMPTY_ICON}>No actions have been performed during this execution.</IconText></Alert>
                    }
                    else {
                        return (
                            <Table {...DEFAULT_TABLE_PROPS}>
                                <thead>
                                    <tr>
                                        <th>Control Point</th>
                                        <th>Action</th>
                                        <th>User</th>
                                        <th>Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        props.execution.actions.map(action => {
                                            return (
                                                <tr key={action.id}>
                                                    <td>{action.controlPoint.energySource.prefix}-{action.controlPoint.number}</td>
                                                    <td>{action.status ? <Badge bg={LOCKED_OUT_COLOR}>Locked Out</Badge> : <Badge bg={ENERGIZED_COLOR}>Re-Energized</Badge>}</td>
                                                    <td>{action.anonymousName} @ {action.anonymousCompanyName}</td>
                                                    <td>{formatDateTime(action.createdOn)}</td>
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                            </Table>
                        )
                    }
                })()
            }
        </PublicLayout>
    )
})

export default function ExecutionPage(...args: Parameters<typeof component>) {
    return component(...args)
}
